<script setup>
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  actionPolicyItems: { type: Array, default: () => [] },
  usage: { type: Object, required: true },
  actionInstructions: { type: Object, required: true },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:usage',
  'update:action-instructions',
  'save',
]);
const { t } = useI18n();

const updateUsage = (key, value) =>
  emit('update:usage', { ...props.usage, [key]: value });

const updateActionInstruction = (key, value) =>
  emit('update:action-instructions', {
    ...props.actionInstructions,
    [key]: value,
  });
</script>

<template>
  <div class="flex flex-col gap-6">
    <div
      class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
    >
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('LUMERAI_SETTINGS.SGP_SETTINGS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-6">
        {{ t('LUMERAI_SETTINGS.SGP_SETTINGS.DESCRIPTION') }}
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div
          v-for="item in props.actionPolicyItems"
          :key="item.setting"
          class="rounded-xl border border-n-strong bg-n-alpha-1 p-4"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <span class="block font-medium text-n-slate-12">{{
                item.title
              }}</span>
              <span class="block text-xs text-n-slate-11 mt-1">{{
                item.description
              }}</span>
            </div>
            <Switch
              :model-value="props.usage[item.setting]"
              @update:model-value="updateUsage(item.setting, $event)"
            />
          </div>

          <TextArea
            v-if="props.usage[item.setting]"
            :model-value="props.actionInstructions[item.instructionKey]"
            class="mt-4"
            :label="t('LUMERAI_SETTINGS.SHOPIFY.BEFORE_ACTION_LABEL')"
            :placeholder="item.placeholder"
            rows="3"
            @update:model-value="
              updateActionInstruction(item.instructionKey, $event)
            "
          />
        </div>
      </div>

      <div
        class="mb-6 rounded-lg border border-n-brand/30 bg-n-brand/5 p-4 text-sm text-n-slate-11"
      >
        <p class="font-medium text-n-slate-12">
          {{ t('LUMERAI_SETTINGS.SGP_SETTINGS.DECISION_TITLE') }}
        </p>
        <p class="mt-1">
          {{ t('LUMERAI_SETTINGS.SGP_SETTINGS.DECISION_DESCRIPTION') }}
        </p>
      </div>

      <TextArea
        :model-value="props.usage.sgp_usage_instruction"
        :label="t('LUMERAI_SETTINGS.SGP_SETTINGS.GENERAL_INSTRUCTION_LABEL')"
        :placeholder="
          t('LUMERAI_SETTINGS.SGP_SETTINGS.GENERAL_INSTRUCTION_PLACEHOLDER')
        "
        rows="4"
        @update:model-value="updateUsage('sgp_usage_instruction', $event)"
      />

      <div class="flex justify-end mt-4">
        <Button
          :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_SGP_PERMISSIONS')"
          color="teal"
          :is-loading="props.isSaving"
          @click="emit('save')"
        />
      </div>
    </div>
  </div>
</template>
